import { axiosInstance } from "api/axios";
import useCustomMutation from "hooks/useCustomMutation";

/**
 * Księgowanie dekretu faktury — REST API (BE: AccountingPostingsController).
 * Trzy operacje na tym samym dekrecie:
 *  - simulate: walidacja (bilans, konta, otwarty okres) bez zapisu,
 *  - buffer:   zapis do bufora księgowania (prowizoryczny, odwracalny),
 *  - commit:   ostateczne zaksięgowanie w dzienniku (nadaje numer wpisu).
 * Kontrakt: docs/backend-invoice-posting.md
 */

export interface IPostingLine {
  accountCode: string; // numer konta (WPK)
  description?: string;
  amountDt: number; // Winien
  amountCt: number; // Ma
}

export interface IPostingRequest {
  /** Faktura rejestrowa (jeśli już zapisana). Przy księgowaniu równoczesnym z
   *  zapisem BE dostaje Id nowo utworzonej faktury. */
  registryInvoiceId?: string;
  operationDate?: string; // data operacji gospodarczej (ISO)
  postingDate?: string; // data księgowania (ISO)
  journal?: string; // rodzaj dziennika (ZAK/SPR/KAS/BAN/PK…) — pusty = BE dobiera
  documentNumber?: string; // numer dowodu księgowego
  netAmount?: number;
  vatAmount?: number;
  grossAmount?: number;
  lines: IPostingLine[];
}

export type PostingStatus = "Simulated" | "Buffer" | "Posted";

export interface IPostingResult {
  postingId: string;
  status: PostingStatus;
  balanced: boolean;
  totalDt: number;
  totalCt: number;
  difference: number;
  journal: string; // kod dziennika
  journalName?: string; // nazwa dziennika (rodzaj dziennika do prezentacji)
  entryDate?: string; // data wpisu w dzienniku (ISO)
  entryNumber?: string; // numer wpisu (dla simulate = proponowany)
  messages: string[];
  /** Commit zaktualizował schemat księgowania kontrahenta (dekret różnił się
   *  od zapisanego schematu). */
  schemeUpdated?: boolean;
}

// BE zwraca PascalCase (PropertyNamingPolicy = null) — mapujemy do camelCase.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapResult = (raw: any): IPostingResult => ({
  postingId: raw?.PostingId ?? "",
  status: (raw?.Status ?? "Simulated") as PostingStatus,
  balanced: Boolean(raw?.Balanced),
  totalDt: raw?.TotalDt ?? 0,
  totalCt: raw?.TotalCt ?? 0,
  difference: raw?.Difference ?? 0,
  journal: raw?.Journal ?? "",
  journalName: raw?.JournalName ?? undefined,
  entryDate: raw?.EntryDate ?? undefined,
  entryNumber: raw?.EntryNumber ?? undefined,
  messages: Array.isArray(raw?.Messages) ? raw.Messages : [],
  schemeUpdated: raw?.SchemeUpdated ?? undefined,
});

const toRequestBody = (req: IPostingRequest) => ({
  RegistryInvoiceId: req.registryInvoiceId,
  OperationDate: req.operationDate,
  PostingDate: req.postingDate,
  Journal: req.journal,
  DocumentNumber: req.documentNumber,
  NetAmount: req.netAmount,
  VatAmount: req.vatAmount,
  GrossAmount: req.grossAmount,
  Lines: req.lines.map((l) => ({
    AccountCode: l.accountCode,
    Description: l.description,
    AmountDt: l.amountDt,
    AmountCt: l.amountCt,
  })),
});

const simulateApi = async (req: IPostingRequest): Promise<IPostingResult> =>
  mapResult((await axiosInstance.post("/accounting/postings/simulate", toRequestBody(req))).data);

const bufferApi = async (req: IPostingRequest): Promise<IPostingResult> =>
  mapResult((await axiosInstance.post("/accounting/postings/buffer", toRequestBody(req))).data);

const commitApi = async (req: IPostingRequest): Promise<IPostingResult> =>
  mapResult((await axiosInstance.post("/accounting/postings", toRequestBody(req))).data);

const postingService = {
  useSimulate: () => useCustomMutation<IPostingResult, IPostingRequest>(simulateApi),
  useBuffer: () => useCustomMutation<IPostingResult, IPostingRequest>(bufferApi),
  useCommit: () => useCustomMutation<IPostingResult, IPostingRequest>(commitApi),
};

export default postingService;
